import { App } from 'antd';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import type { BtlApplication, CreateApplicationToSolicitorDto } from '@api/btlApplicationForm/types';
import { SolicitorType } from '@api/btlApplicationForm/types';
import {
  getAdjacentOoCaseSidebarItemId,
  getAdjacentOoCaseSidebarUrlSection,
  getOoCaseSidebarNavigationLabel,
} from '@components/CaseSidebar/navigation/ooCaseSidebarNavigation';
import type { OoCaseSidebarContext } from '@components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationContext } from '@hooks/useBtlFormValidation';
import { makeFormReadonly } from '@pages/BtlApplicationFormPage/makeFormReadonly';
import { solicitorsFormSchema, WayToSelectSolicitor } from '@pages/BtlApplicationFormPage/Solicitors/formSchema';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { standardFormValidationProps } from '@common/formValidation';

interface UseOoCaseSolicitorsFormParams {
  caseId: string;
  sidebarContext: OoCaseSidebarContext;
}

export function isOoSolicitorsFormEditable(caseData?: BtlApplication): boolean {
  return caseData?.status === 'DIP_ACCEPTED';
}

function mapSolicitorToFormValues(data?: BtlApplication) {
  if (!data?.customerSolicitor?.solicitorFirm) {
    return {};
  }

  const { solicitor, solicitorFirm } = data.customerSolicitor;

  const firmName = solicitorFirm.name;
  const emailAddress = solicitorFirm.emailAddress;
  const phoneNumber = solicitorFirm.phoneNumber;
  const addressParsed = {
    fullAddress: JSON.stringify(solicitorFirm.address),
    ...solicitorFirm.address,
  };
  const solicitorName = solicitor ? `${solicitor.firstName} ${solicitor.lastName}` : '';

  const isPanelMember = solicitorFirm.isPanelMember || false;
  const recordId = solicitor?.id ? `${solicitorFirm.id}-${solicitor.id}` : solicitorFirm.id;
  const wayToSelectSolicitor = isPanelMember
    ? WayToSelectSolicitor.PANEL_MEMBER
    : recordId
    ? WayToSelectSolicitor.NON_PANEL_MEMBER
    : undefined;

  return {
    firmName,
    emailAddress,
    phoneNumber,
    address: { ...addressParsed, isManual: true },
    solicitorName,
    wayToSelectSolicitor,
    panelMember: isPanelMember && recordId ? recordId : undefined,
    registeredName: !isPanelMember && recordId ? recordId : undefined,
  };
}

export function useOoCaseSolicitorsForm({ caseId, sidebarContext }: UseOoCaseSolicitorsFormParams) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const { data: caseData, isLoading: isCaseLoading } = useBtlApplicationInProgressById(caseId);
  const formValidationContext = useContext(FormValidationContext);
  const formSectionParam = searchParams.get('section') ?? CaseSidebarItemId.SOLICITORS;
  const isFormEditable = isOoSolicitorsFormEditable(caseData);
  const nextSection = getAdjacentOoCaseSidebarUrlSection(formSectionParam, sidebarContext, 'next') ?? '';
  const showSkipButton = isFormEditable && Boolean(nextSection);

  if (!formValidationContext) {
    throw new Error('OO solicitors form must be used within a FormValidationProvider');
  }

  const { setIsSubmitting } = formValidationContext;

  if (isCaseLoading) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    isFormEditable,
  };

  const formSchema = solicitorsFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    isFormEditable,
    showSkipButton,
    footerProps: {
      previousSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'previous'),
      nextSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'next'),
      previousLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'previous'),
      nextLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'next'),
    },
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      name: 'oo_solicitors',
      ...standardFormValidationProps,
      request: async () => mapSolicitorToFormValues(caseData),
      onFinish: async (values: Record<string, unknown>) => {
        try {
          setIsSubmitting(true);

          if (values.isManualSolicitor) {
            const address = values.address as {
              fullAddress?: string;
              addressLine1?: string;
              addressLine2?: string;
              town?: string;
              townOrCity?: string;
              country?: string;
              postcode?: string;
            };
            const addressParsed = JSON.parse(address?.fullAddress || '{}');
            const addressData = addressParsed.addressLine1 ? addressParsed : address;
            const body: CreateApplicationToSolicitorDto = {
              firmName: values.firmName as string,
              solicitorName: values.solicitorName as string | undefined,
              solicitorType: SolicitorType.CUSTOMER,
              addressLine1: addressData.addressLine1,
              addressLine2: addressData.addressLine2,
              town: addressParsed.addressLine1 ? addressData.townOrCity : addressData.town,
              country: addressData.country,
              postcode: addressData.postcode,
              emailAddress: values.emailAddress as string,
              phoneNumber: values.phoneNumber as string,
            };

            await btlApplicationFormApi.createSolicitor(caseId, body);
          } else if (values.wayToSelectSolicitor === WayToSelectSolicitor.PANEL_MEMBER) {
            const [solicitorFirmId, solicitorId] = (values.panelMember as string | undefined)?.split('-') || [];

            await btlApplicationFormApi.linkSolicitor(caseId, {
              solicitorId,
              solicitorFirmId,
              solicitorType: SolicitorType.CUSTOMER,
            });
          } else if (values.wayToSelectSolicitor === WayToSelectSolicitor.NON_PANEL_MEMBER) {
            const [solicitorFirmId, solicitorId] = (values.registeredName as string | undefined)?.split('-') || [];

            await btlApplicationFormApi.linkSolicitor(caseId, {
              solicitorId,
              solicitorFirmId,
              solicitorType: SolicitorType.CUSTOMER,
            });
          }

          if (nextSection) {
            navigate({ search: `?section=${nextSection}` });
          }
        } catch (error: unknown) {
          console.error(error);
          message.error(error instanceof Error ? error.message : 'Failed to save solicitor details');
        } finally {
          setIsSubmitting(false);
        }
      },
    },
  };
}
